import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { City } from '@/world/city';
import type { PreferenceKey } from '@/core/types';

/**
 * Fully procedural ambient sound — no audio assets ship with the game. A slow,
 * detuned drone forms the bed; its brightness and level breathe with the city's
 * vitality, so a thriving city hums warmer and a stagnant one thins to near
 * silence. Each act of evolution rings a soft, tuned bell whose pitch encodes
 * which preference drove it, turning the city's decisions into a faint music.
 *
 * The context is created lazily on the first user gesture (pointer lock) to honour
 * browser autoplay policy, and the whole system degrades to a silent no-op if the
 * Web Audio API is unavailable.
 */
const PENTATONIC = [0, 3, 5, 7, 10]; // minor pentatonic degrees (semitones)
const DRIVER_DEGREE: Record<PreferenceKey, number> = {
  verticality: 12,
  parkour: 10,
  velocity: 7,
  exploration: 5,
  intimacy: 3,
  contemplation: 0,
  transgression: 1,
};

export class AudioSystem implements System {
  readonly name = 'audio';

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private droneGain: GainNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private voices: OscillatorNode[] = [];
  private started = false;
  /** While true, the finale ceremony owns the filter/master and update() stands
   *  down so its per-frame targets don't fight the ceremony sweep. */
  private ceremonyOverride = false;

  constructor(
    private engine: Engine,
    private city: City,
  ) {}

  init(): void {
    this.engine.events.on('app:pointerlock', ({ locked }) => {
      if (locked) this.ensureStarted();
    });
    this.engine.events.on('city:evolved', ({ driver }) => this.chime(driver));
    this.engine.events.on('finale:phase', ({ phase }) => this.onFinalePhase(phase));
  }

  /**
   * The ceremony breathes with the sound: at the recall the bed sinks to a muffled
   * hush (low-pass swept down, level dropped); at the rebloom it opens bright again
   * and a rising pentatonic cascade rings out as the city returns; by done, normal
   * vitality-driven behaviour has fully resumed.
   */
  private onFinalePhase(phase: import('@/core/types').FinalePhase): void {
    if (!this.ctx || !this.filter || !this.master) return;
    const t = this.ctx.currentTime;
    if (phase === 'recall') {
      this.ceremonyOverride = true;
      // ~3s to settle (setTargetAtTime reaches ~95% in 3× the time constant).
      this.filter.frequency.setTargetAtTime(180, t, 1.0);
      this.master.gain.setTargetAtTime(0.12, t, 1.0);
    } else if (phase === 'rebloom') {
      this.ceremonyOverride = false; // hand control back to update()
      // A bright swell to greet the returning city, then the chime cascade.
      this.filter.frequency.setTargetAtTime(1600, t, 1.2);
      this.master.gain.setTargetAtTime(0.5, t, 1.5);
      this.chimeCascade();
    } else if (phase === 'done') {
      this.ceremonyOverride = false;
    }
  }

  /** Five ascending pentatonic chimes ~0.5s apart — the city, singing back. */
  private chimeCascade(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime + 0.1;
    for (let i = 0; i < 5; i++) {
      const semis = PENTATONIC[i % PENTATONIC.length];
      const freq = 330 * Math.pow(2, semis / 12); // 0,3,5,7,10 → monotonic ascent
      this.voiceAt(freq, t0 + i * 0.5);
    }
  }

  /** A single soft triangle bell scheduled at an absolute context time. */
  private voiceAt(freq: number, start: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0;
    osc.connect(g);
    g.connect(master);
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.12, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 2.0);
    osc.start(start);
    osc.stop(start + 2.1);
  }

  private ensureStarted(): void {
    if (this.started) {
      void this.ctx?.resume();
      return;
    }
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.started = true;
    const ctx = new Ctor();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0.0;
    this.master.connect(ctx.destination);

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 500;
    this.filter.Q.value = 0.6;
    this.filter.connect(this.master);

    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0.14;
    this.droneGain.connect(this.filter);

    // A quiet chord: root, fifth, octave, softly detuned for movement.
    const base = 55; // A1
    const ratios = [1, 1.5, 2, 3.001];
    for (const r of ratios) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = base * r;
      osc.detune.value = (Math.sin(r * 12.9) * 8);
      osc.connect(this.droneGain);
      osc.start();
      this.voices.push(osc);
    }

    // Fade the master in gently.
    this.master.gain.setTargetAtTime(0.5, ctx.currentTime, 2.5);
  }

  update(_time: Time): void {
    if (!this.ctx || !this.filter || !this.master) return;
    // Stand down while the ceremony sweeps the filter/master itself.
    if (this.ceremonyOverride) return;
    const t = this.ctx.currentTime;
    const v = this.city.vitality;
    const m = this.city.maturity;
    // Brighter and slightly louder as the city lives; opens up as it matures.
    this.filter.frequency.setTargetAtTime(260 + v * 900 + m * 400, t, 0.6);
    this.master.gain.setTargetAtTime(0.28 + v * 0.34, t, 1.2);
  }

  private chime(driver: PreferenceKey): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    const degree = DRIVER_DEGREE[driver] ?? 0;
    // Snap to a pleasant pentatonic pitch above the drone.
    const oct = PENTATONIC[degree % PENTATONIC.length];
    const freq = 220 * Math.pow(2, (degree + oct) / 12);

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0;
    osc.connect(g);
    g.connect(master);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.12, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
    osc.start(t);
    osc.stop(t + 2.5);
  }

  dispose(): void {
    for (const v of this.voices) {
      try {
        v.stop();
      } catch {
        /* already stopped */
      }
    }
    void this.ctx?.close();
  }
}
